import type { DetailedWalletTransaction } from '../types.ts';

export type OutcomeTradeLedgerEntry =
  | {
      kind: 'buy';
      id: string;
      at: string;
      /** Total ZAR debited from wallet (absolute value) */
      debitTotal: number;
      /** user_bets.id when known — dedupe across wallet vs position rows */
      relatedBetId?: string;
      source?: 'wallet' | 'position';
    }
  | {
      kind: 'sell';
      id: string;
      at: string;
      /** Net ZAR credited after performance fee */
      netCredit: number;
    };

function outcomeNamesMatch(txOutcome: string | undefined, positionOutcome: string): boolean {
  if (txOutcome == null || txOutcome === '') return false;
  return txOutcome.trim().toLowerCase() === positionOutcome.trim().toLowerCase();
}

/** Parse option name from LMSR/pool wallet copy: Trade on "yes" / Bet placed on "yes". */
export function parseOutcomeFromBetPlacementDescription(desc?: string | null): string | undefined {
  if (desc == null || desc === '') return undefined;
  const tradeOn = desc.match(/Trade on "([^"]+)"/i)?.[1];
  const betPlaced = desc.match(/Bet placed on "([^"]+)"/i)?.[1];
  const r = tradeOn ?? betPlaced;
  return r?.trim();
}

/**
 * Wallet-backed history for one market outcome (LMSR buys + sells share this shape).
 * Buys match by enriched market/outcome, by bet id (UUID string-normalized), or Trade-on description.
 * Sorted oldest → newest.
 */
export function buildOutcomeTradeLedger(
  marketId: string,
  outcomeName: string,
  transactions: DetailedWalletTransaction[],
  betIdsForOutcome?: ReadonlySet<string>,
): OutcomeTradeLedgerEntry[] {
  const entries: OutcomeTradeLedgerEntry[] = [];
  const mid = String(marketId);

  const betIdSet =
    betIdsForOutcome != null
      ? new Set([...betIdsForOutcome].map((id) => String(id)))
      : null;

  for (const tx of transactions) {
    const at = tx.completed_at ?? tx.created_at;
    if (!at) continue;

    if (String(tx.type ?? '').trim() === 'bet_placement') {
      const txMid = tx.marketId != null ? String(tx.marketId) : '';
      const descOutcome = parseOutcomeFromBetPlacementDescription(tx.description);

      const matchByMarketOutcome =
        txMid === mid && outcomeNamesMatch(tx.outcomeName, outcomeName);

      const rid = tx.related_entity_id != null ? String(tx.related_entity_id) : '';
      const matchByBetId = betIdSet != null && rid !== '' && betIdSet.has(rid);

      const matchByDescription =
        txMid === mid &&
        descOutcome !== undefined &&
        outcomeNamesMatch(descOutcome, outcomeName);

      if (matchByMarketOutcome || matchByBetId || matchByDescription) {
        entries.push({
          kind: 'buy',
          id: `buy-${tx.id}`,
          at,
          debitTotal: Math.abs(Number(tx.amount)),
          relatedBetId: rid || undefined,
          source: 'wallet',
        });
      }
      continue;
    }

    if (
      tx.type === 'payout' &&
      tx.related_entity_type === 'market' &&
      tx.related_entity_id != null &&
      String(tx.related_entity_id) === mid &&
      outcomeNamesMatch(tx.outcomeName, outcomeName) &&
      (tx.description?.includes('Sold shares on') ?? false)
    ) {
      entries.push({
        kind: 'sell',
        id: `sell-${tx.id}`,
        at,
        netCredit: Math.max(0, Number(tx.amount)),
      });
    }
  }

  return entries.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

/** Minimal slice of a portfolio row used to synthesize missing LMSR buy legs. */
export type LmsrLedgerPositionInput = {
  betId: string;
  engine_type?: string;
  option_name: string;
  betTime?: string;
  shares_owned?: number | null;
  amount?: number;
  amountAfterFee?: number;
  lmsr_initial_stake_net?: number | null;
  lmsr_initial_stake_gross?: number | null;
};

/**
 * When wallet_tx rows fail to attach to the outcome (timeouts, enrichment edge cases),
 * synthesize Buy lines from LMSR snapshots on user_bets so every purchase still appears.
 * Skips buys already represented in the wallet ledger (`relatedBetId`).
 */
export function supplementLmsrLedgerWithPositionBuys(
  ledger: OutcomeTradeLedgerEntry[],
  positions: readonly LmsrLedgerPositionInput[],
  outcomeName: string,
): OutcomeTradeLedgerEntry[] {
  const out: OutcomeTradeLedgerEntry[] = [...ledger];

  const walletBuyBetIds = new Set<string>();
  for (const e of out) {
    if (e.kind === 'buy' && e.relatedBetId) walletBuyBetIds.add(String(e.relatedBetId));
  }

  for (const p of positions) {
    if (p.engine_type !== 'lmsr') continue;
    if (!outcomeNamesMatch(p.option_name, outcomeName)) continue;

    const bid = String(p.betId);
    if (walletBuyBetIds.has(bid)) continue;

    const iniN = Number(p.lmsr_initial_stake_net ?? NaN);
    const iniG = Number(p.lmsr_initial_stake_gross ?? NaN);
    const aaf = Number(p.amountAfterFee ?? NaN);
    const gross = Number(p.amount ?? NaN);
    const sh = Number(p.shares_owned ?? 0);

    let debit = 0;
    if (Number.isFinite(iniN) && iniN > 1e-6) debit = iniN;
    else if (Number.isFinite(iniG) && iniG > 1e-6) debit = iniG;
    else if (Number.isFinite(aaf) && aaf > 1e-6) debit = aaf;
    else if (Number.isFinite(gross) && gross > 1e-6) debit = gross;

    if (debit <= 1e-6 && sh > 1e-9) {
      debit = Math.max(Math.abs(aaf || 0), Math.abs(gross || 0), debit);
    }
    if (debit <= 1e-6) continue;

    out.push({
      kind: 'buy',
      id: `pos-buy-${bid}`,
      at: p.betTime ?? '1970-01-01T00:00:00.000Z',
      debitTotal: debit,
      relatedBetId: bid,
      source: 'position',
    });
  }

  return out.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}
